/* eslint-disable consistent-return */
/* eslint-disable no-console */
import {
  take,
  call,
  put,
  select,
  takeLatest,
  fork,
  race,
  all,
} from 'redux-saga/effects';
import { history } from 'utils/history';
import axios from 'constants/axios';
import { statusIsOk } from 'utils/api';
import {
  isLoggingInToEmbeddedApp,
  notifyLoginSuccessToParentAndClose,
} from 'utils/embedding';
import { isSafeRedirectEnabled } from 'utils/flags/isSafeRedirectEnabled';
import { safeRedirectPath, safeRedirectURL } from 'utils/safeRedirectPath';
import isEmail from 'validator/lib/isEmail';
import { selectRedirectedFrom } from 'containers/App/selectors';
import { fetchWhiteLabelling } from 'containers/WhiteLabelling/_utils/actions';

import { LOGOUT } from 'containers/Logout/actions';

import {
  API_CHECK_USERNAME,
  API_LOGIN,
  API_FORGOT_PASSWORD,
  API_REFRESH_TOKEN,
  WHITE_LABELLING_SOURCES,
  API_IMPORT_SOLUTION,
} from 'constants/appConstants';

/* Constants */
import {
  SUBMIT_USERNAME,
  LOGIN,
  LOGIN_SUCCESS,
  FORGOT_PASSWORD,
  SUBMIT_TWO_FA_CODE,
} from './constants';

/* Selectors */
import {
  selectUsername,
  selectPassword,
  selectTwoFaCode,
  selectUserId,
} from './selectors';

/* Actions */
import {
  submitUsernameSuccess,
  submitUsernameFailure,
  loginSuccess,
  loginFailure,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  requireTwoFaCode,
} from './actions';

import { setUser, getUser } from '../../auth';

const buildSAMLLoginURL = (identityProviderId, redirectTo) => {
  const prefix = `/api/v1/auth/login/sso/${identityProviderId}`;
  if (redirectTo) {
    return `${prefix}?redirect_to=${redirectTo}`;
  }
  return prefix;
};

export function* submitUsernameSaga() {
  try {
    const username = yield select(selectUsername());

    const response = yield call(axios, {
      method: 'post',
      url: API_CHECK_USERNAME,
      data: {
        username,
      },
    });

    if (statusIsOk(response)) {
      if (response.data.meta.type === 'saml') {
        // TODO clean up feature flag in DEV-21085
        if (isSafeRedirectEnabled()) {
          const preSSORedirect = safeRedirectURL(
            new URLSearchParams(window.location.search).get('redirect') ?? ''
          );

          window.location = buildSAMLLoginURL(
            response.data.meta.identityProviderId,
            preSSORedirect
          );
        } else {
          const redirectParam = new URLSearchParams(window.location.search).get(
            'redirect'
          );
          const redirectTo = `${window.location.origin}${redirectParam ?? ''}`;

          window.location = buildSAMLLoginURL(
            response.data.meta.identityProviderId,
            redirectTo
          );
        }
      } else {
        yield put(submitUsernameSuccess(response.data.meta.type));
      }
    } else {
      yield put(submitUsernameFailure(response.data.error));
    }
  } catch (err) {
    if (err.code === 'ECONNABORTED') {
      yield put(
        submitUsernameFailure('Authentication server is not available')
      );
    } else {
      // Do not log the raw error — its request config carries the username.
      console.error('Username check failed', err?.message);
      yield put(submitUsernameFailure('Something went wrong'));
    }
  }
}

export function* loginSaga() {
  try {
    const username = yield select(selectUsername());
    const password = yield select(selectPassword());

    const twoFaCode = yield select(selectTwoFaCode());
    const userId = yield select(selectUserId());

    const isTwoFaSubmission = !!twoFaCode && !!userId;

    const data = isTwoFaSubmission
      ? { userId, twoFaCode }
      : { username, password };

    const response = yield call(axios, {
      method: 'post',
      url: API_LOGIN,
      data,
    });

    if (statusIsOk(response)) {
      if (response.data.status === '2fa') {
        yield put(requireTwoFaCode(response.data.userId));
        return;
      }
      const { user } = response.data;

      yield call(setUser, user);

      if (isLoggingInToEmbeddedApp()) {
        notifyLoginSuccessToParentAndClose();
        return;
      }

      // TODO clean up feature flag in DEV-21085
      const redirectFromUrl = isSafeRedirectEnabled()
        ? safeRedirectPath(
            new URLSearchParams(window.location.search).get('redirect') ?? ''
          )
        : new URLSearchParams(window.location.search).get('redirect') ?? '';
      const redirectedFrom =
        redirectFromUrl || (yield select(selectRedirectedFrom()));

      if (redirectedFrom) {
        // Full-page nav lets Kong route non-SPA /api-docs requests. Anchor to a
        // path boundary so '/api-docs.evil.com' can't force an off-target nav.
        if (/^\/api-docs($|[/?#])/.test(redirectedFrom)) {
          window.location.href = redirectedFrom;
          return true;
        }
        // If redirected to login, redirect to previous page
        yield call([history, history.push], redirectedFrom);
      } else if (user.lastLoginAt === null && window.TRIAL_INSTANCE) {
        // import solution
        const importResponse = yield call(axios, {
          method: 'post',
          url: API_IMPORT_SOLUTION(),
          data: {
            code: 'AliZNbUHl9mlbX78svdOUZp2pqwixz/a6zIPg7Qn+wg2XZb89QWfJ/X+OrBg1XhMckkiPv+wEQz2TYCpmi/2c9/j/JI3HrQgjaf/+T0rJkX2bwc5NzjeXgxXmt1cAAQO5mqRPFn9h0TM43iL7X9kf0G49OSsW5o=',
          },
        });
        if (statusIsOk(importResponse)) {
          const solutionUUID = importResponse.data.result.solution.uuid;
          const solutionVersionUUID =
            importResponse.data.result.solutionVersion.uuid;

          // get solution detail
          const getSolutionResponse = yield call(axios, {
            method: 'get',
            url: `solutions/${solutionUUID}`,
          });
          if (statusIsOk(getSolutionResponse)) {
            const moduleId =
              getSolutionResponse.data.result.solutionVersions[0].modules[0]
                .module_id;
            const moduleVersionId =
              getSolutionResponse.data.result.solutionVersions[0].modules[0]
                .module_version_id;

            // redirect user to the solution
            yield call(
              [history, history.push],
              `/app/solution/${solutionUUID}/version/${solutionVersionUUID}/app/${moduleId}/studio/${moduleVersionId}/editor`
            );
          } else {
            console.log('Error loading solution info');
            yield all([
              put(loginSuccess()),
              call([history, history.push], '/app/inbox'),
            ]);
          }
        } else {
          console.log('Error importing solution');
          yield all([
            put(loginSuccess()),
            call([history, history.push], '/app/inbox'),
          ]);
        }
      } else {
        yield all([
          put(loginSuccess()),
          call([history, history.push], '/app/inbox'),
        ]);
      }

      return true;
    }
    yield put(loginFailure(response.data.error));
    return undefined;
  } catch (err) {
    if (err.code === 'ECONNABORTED') {
      yield put(loginFailure('Authentication server is not available'));
    } else {
      // Do not log the raw error — its request config carries credentials.
      console.error('Login failed', err?.message);
      yield put(loginFailure('Something went wrong'));
    }
  }

  return undefined;
}

export function* logout() {
  console.log('Redirecting to logout!');
  yield call([history, history.push], '/logout');
}

export function* refreshAccessToken() {
  try {
    const response = yield call(axios, {
      method: 'post',
      url: API_REFRESH_TOKEN,
    });

    return statusIsOk(response) ? true : undefined;
  } catch (err) {
    console.error(err.message);
    console.error('Could not refresh accessToken! Logging out!');
    return undefined;
  }
}

export function* forgotPasswordSaga() {
  try {
    const username = yield select(selectUsername());

    if (!isEmail(username)) {
      yield put(
        forgotPasswordFailure(`${username} is not a valid email address`)
      );
    } else {
      const response = yield call(axios, {
        method: 'post',
        url: API_FORGOT_PASSWORD,
        data: {
          username,
        },
      });

      if (statusIsOk(response)) {
        yield put(forgotPasswordSuccess(response.data.meta));
      } else {
        yield put(forgotPasswordFailure(response.data.error));
      }
    }
  } catch (err) {
    // Do not log the raw error — its request config carries the username (PII).
    console.error('Forgot password request failed', err?.message);
    yield all([
      put(forgotPasswordFailure('Could not reset password. Please try later')),
    ]);
  }
}

export function* submitUsernameWatcher() {
  yield takeLatest(SUBMIT_USERNAME, submitUsernameSaga);
}

export function* forgotPasswordWatcher() {
  yield takeLatest(FORGOT_PASSWORD, forgotPasswordSaga);
}

export function* authSaga() {
  while (true) {
    let loggedIn;

    const loginActions = [
      // eslint-disable-next-line redux-saga/yield-effects
      take(LOGIN),
    ];

    const { shouldLogin, loggedInWithTokens, shouldLoginWithTwoFa } =
      yield race({
        shouldLogin: all(loginActions), // Normal login
        shouldLoginWithTwoFa: take(SUBMIT_TWO_FA_CODE), // Two-factor authentication login
        loggedInWithTokens: take(LOGIN_SUCCESS), // SSO
      });

    if (shouldLogin || shouldLoginWithTwoFa) {
      loggedIn = yield call(loginSaga);
    } else {
      loggedIn = loggedInWithTokens;
    }

    if (loggedIn) {
      let userLoggedOut;
      const user = getUser();

      if (user) {
        yield put(
          fetchWhiteLabelling(
            WHITE_LABELLING_SOURCES.ACCOUNT_ID,
            user.accountId
          )
        );
      }

      while (!userLoggedOut) {
        const { loggedOut } = yield race({
          loggedOut: take(LOGOUT),
        });

        // break the loop
        userLoggedOut = true;
        yield call(logout);
      }
    }
  }
}

export function* rootSaga() {
  yield all([fork(submitUsernameWatcher), fork(forgotPasswordWatcher)]);
}

// All sagas to be loaded
export default [rootSaga];
